import React from 'react';
import { CrmApiRepository } from '@vanilla/services/crm/CrmApiRepository.js';
import { UserService } from '@vanilla/services/UserService.js';
import { PipelineStore } from '@vanilla/constants/Pipelines.js';
import { STAGE_ORDER, getStageLabel } from '@vanilla/constants/Stages.js';
import { DEAL_TYPE_ORDER, getDealTypeLabel } from '@vanilla/constants/DealTypes.js';
import { LEAD_SOURCE_ORDER, getLeadSourceLabel } from '@vanilla/constants/LeadSources.js';
import { SERVICE_LINE_ORDER, getServiceLineLabel } from '@vanilla/constants/ServiceLines.js';
import { ENGAGEMENT_MODEL_ORDER, getEngagementModelLabel } from '@vanilla/constants/EngagementModels.js';
import { FieldHelpIcon } from '@/components/ui/FieldHelpIcon.jsx';
import { Input } from '@/components/ui/input';
import { DealFormSelect, DealQuickCreateField, DealPipelineQuickCreateField } from './DealFormSelect.jsx';
import { CURRENCIES } from './dealFormUtils.js';

export default function DealFormDetailsPanel({
    form,
    errors,
    setField,
    setClients,
    setUsers,
    setContacts,
    setAllContacts,
    setPipelines,
    clients,
    users,
    contacts,
    allContacts,
    pipelineOptions,
    canAssignOwner,
    handleStageChange,
    handleProbabilityCommit,
}) {
    return (
        <div className="premium-card" id="sectionDetailsCard" data-stepper-panel="details">
            <div className="premium-card-header">
                <h3 className="premium-card-title"><span className="premium-card-number">1</span> Deal Details</h3>
                <p className="premium-card-subtitle">Essential information to create and track this opportunity.</p>
            </div>
            <div id="dealDetailsContent">
                <div className="deal-form-grid-1">
                    <div className="deal-form-inline-pair inline-pair-name-date">
                        <div className="form-group" data-field="dealName">
                            <label htmlFor="dealName">Deal Name <span className="required-asterisk">*</span> <FieldHelpIcon fieldId="dealName" /></label>
                            <Input unstyled type="text" id="dealName" className="form-input" autoComplete="off" placeholder="Enter deal name" value={form.dealName} onChange={(e) => setField('dealName', e.target.value)} />
                            {errors.dealName && <span className="field-error">{errors.dealName}</span>}
                        </div>
                        <div className="form-group" data-field="dealDate">
                            <label htmlFor="dealDate">Deal Created <FieldHelpIcon fieldId="dealDate" /></label>
                            <Input unstyled type="date" className="form-input" value={form.dealDate} onChange={(e) => setField('dealDate', e.target.value)} />
                        </div>
                    </div>
                    <div className="form-group full-width" data-field="dealClientId">
                        <label htmlFor="dealClientId">Account Name <span className="required-asterisk">*</span> <FieldHelpIcon fieldId="dealClientId" /></label>
                        <DealQuickCreateField
                            entityType="account"
                            value={form.dealClientId}
                            onCreated={async (entity) => {
                                const list = await CrmApiRepository.getAllClients().catch(() => []);
                                setClients(list);
                                setField('dealClientId', entity.id);
                            }}
                        >
                            <DealFormSelect
                                id="dealClientId"
                                value={form.dealClientId}
                                placeholder="Select account"
                                onValueChange={(v) => setField('dealClientId', v)}
                                options={clients.map((c) => ({ value: c.id, label: c.name }))}
                            />
                        </DealQuickCreateField>
                        {errors.dealClientId && <span className="field-error">{errors.dealClientId}</span>}
                    </div>
                    <div className="form-group full-width" data-field="dealOwnerUserId">
                        <label htmlFor="dealOwnerUserId">Sales Representative <span className="required-asterisk">*</span> <FieldHelpIcon fieldId="dealOwnerUserId" /></label>
                        <DealQuickCreateField
                            entityType="user"
                            value={form.dealOwnerUserId}
                            onCreated={async (entity) => {
                                const list = await UserService.getAssignableUsers().catch(() => []);
                                setUsers(list);
                                setField('dealOwnerUserId', entity.id);
                            }}
                        >
                            <DealFormSelect
                                id="dealOwnerUserId"
                                value={form.dealOwnerUserId}
                                placeholder="Select sales representative"
                                disabled={!canAssignOwner}
                                onValueChange={(v) => setField('dealOwnerUserId', v)}
                                options={users.map((u) => ({ value: u.id, label: u.name || u.email || u.id }))}
                            />
                        </DealQuickCreateField>
                        {errors.dealOwnerUserId && <span className="field-error">{errors.dealOwnerUserId}</span>}
                    </div>
                    <div className="deal-form-inline-pair inline-pair-currency-amount">
                        <div className="form-group" data-field="dealCurrency">
                            <label htmlFor="dealCurrency">Currency <FieldHelpIcon fieldId="dealCurrency" /></label>
                            <DealFormSelect
                                id="dealCurrency"
                                value={form.dealCurrency}
                                allowEmpty={false}
                                onValueChange={(v) => setField('dealCurrency', v)}
                                options={CURRENCIES}
                            />
                        </div>
                        <div className="form-group" data-field="dealAmount">
                            <label htmlFor="dealAmount">Total Deal Amount <span className="required-asterisk">*</span> <FieldHelpIcon fieldId="dealAmount" /></label>
                            <Input unstyled type="text" id="dealAmount" className="form-input" inputMode="decimal" autoComplete="off" placeholder="Enter total deal amount" value={form.dealAmount} onChange={(e) => setField('dealAmount', e.target.value.replace(/[^0-9.]/g, ''))} />
                            {errors.dealAmount && <span className="field-error">{errors.dealAmount}</span>}
                        </div>
                    </div>
                    <div className="form-group full-width" data-field="dealStage">
                        <label htmlFor="dealStage">Stage <span className="required-asterisk">*</span> <FieldHelpIcon fieldId="dealStage" /></label>
                        <DealFormSelect
                            id="dealStage"
                            value={form.dealStage}
                            placeholder="Select stage"
                            onValueChange={handleStageChange}
                            options={STAGE_ORDER.map((s) => ({ value: s, label: getStageLabel(s) }))}
                        />
                        {errors.dealStage && <span className="field-error">{errors.dealStage}</span>}
                    </div>
                    <div className="form-group full-width" data-field="dealProbability">
                        <label htmlFor="dealProbabilitySlider">Probability <FieldHelpIcon fieldId="dealProbability" /></label>
                        <div className="probability-control">
                            <div className="probability-slider-wrap">
                                <input
                                    type="range"
                                    id="dealProbabilitySlider"
                                    className="probability-slider"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={form.dealProbability}
                                    aria-label="Probability percentage"
                                    onChange={(e) => setField('dealProbability', Number(e.target.value))}
                                    onPointerUp={(e) => handleProbabilityCommit(e.currentTarget.value)}
                                    onKeyUp={(e) => handleProbabilityCommit(e.currentTarget.value)}
                                />
                                <span className="probability-slider-value" id="dealProbabilityValue">{form.dealProbability}%</span>
                            </div>
                        </div>
                    </div>
                    <div className="form-group full-width" data-field="expectedCloseDate">
                        <label htmlFor="expectedCloseDate">Expected Close Date <FieldHelpIcon fieldId="expectedCloseDate" /></label>
                        <Input unstyled type="date" className="form-input" value={form.expectedCloseDate} onChange={(e) => setField('expectedCloseDate', e.target.value)} />
                    </div>
                </div>

                <p className="deal-form-section-hint">Optional classification and relationship fields.</p>
                <div className="deal-form-grid-1">
                    <div className="form-group full-width" data-field="dealType">
                        <label htmlFor="dealType">Deal Type <FieldHelpIcon fieldId="dealType" /></label>
                        <DealFormSelect
                            id="dealType"
                            value={form.dealType}
                            placeholder="Select deal type"
                            onValueChange={(v) => setField('dealType', v)}
                            options={DEAL_TYPE_ORDER.map((t) => ({ value: t, label: getDealTypeLabel(t) }))}
                        />
                    </div>
                    <div className="form-group full-width" data-field="dealEngagementModel">
                        <label htmlFor="dealEngagementModel">Engagement Model <FieldHelpIcon fieldId="dealEngagementModel" /></label>
                        <DealFormSelect
                            id="dealEngagementModel"
                            value={form.dealEngagementModel}
                            placeholder="Select engagement model"
                            onValueChange={(v) => setField('dealEngagementModel', v)}
                            options={ENGAGEMENT_MODEL_ORDER.map((m) => ({ value: m, label: getEngagementModelLabel(m) }))}
                        />
                    </div>
                    <div className="form-group full-width" data-field="dealServiceLine">
                        <label htmlFor="dealServiceLine">Service Line <FieldHelpIcon fieldId="dealServiceLine" /></label>
                        <DealFormSelect
                            id="dealServiceLine"
                            value={form.dealServiceLine}
                            placeholder="Select service line"
                            onValueChange={(v) => setField('dealServiceLine', v)}
                            options={SERVICE_LINE_ORDER.map((s) => ({ value: s, label: getServiceLineLabel(s) }))}
                        />
                    </div>
                    <div className="form-group full-width" data-field="dealPipeline">
                        <label htmlFor="dealPipeline">Pipeline <span className="required-asterisk">*</span> <FieldHelpIcon fieldId="dealPipeline" /></label>
                        <DealPipelineQuickCreateField
                            value={form.dealPipeline}
                            onSaved={async (entity) => {
                                setPipelines(PipelineStore.getAll());
                                if (entity?.id) setField('dealPipeline', entity.id);
                            }}
                        >
                            <DealFormSelect
                                id="dealPipeline"
                                value={form.dealPipeline}
                                placeholder="Select pipeline"
                                onValueChange={(v) => setField('dealPipeline', v)}
                                options={pipelineOptions.map((p) => ({ value: p.id, label: p.name }))}
                            />
                        </DealPipelineQuickCreateField>
                        {errors.dealPipeline && <span className="field-error">{errors.dealPipeline}</span>}
                    </div>
                    <div className="form-group full-width" data-field="dealCampaignSource">
                        <label htmlFor="dealCampaignSource">Campaign Source <FieldHelpIcon fieldId="dealCampaignSource" /></label>
                        <Input unstyled type="text" id="dealCampaignSource" className="form-input" placeholder="Enter campaign source" value={form.dealCampaignSource} onChange={(e) => setField('dealCampaignSource', e.target.value)} />
                    </div>
                    <div className="form-group full-width" data-field="dealLeadSource">
                        <label htmlFor="dealLeadSource">Lead Source <FieldHelpIcon fieldId="dealLeadSource" /></label>
                        <DealFormSelect
                            id="dealLeadSource"
                            value={form.dealLeadSource}
                            placeholder="Select lead source"
                            onValueChange={(v) => setField('dealLeadSource', v)}
                            options={LEAD_SOURCE_ORDER.map((s) => ({ value: s, label: getLeadSourceLabel(s) }))}
                        />
                    </div>
                    {['referral','client_referral','partner_referral','employee_personal_referral'].includes(form.dealLeadSource) && (
                        <div className="form-group full-width" data-field="referralContactId">
                            <label htmlFor="referralContactId">Referrer Contact <span className="required-asterisk">*</span></label>
                            <DealQuickCreateField entityType="contact" value={form.referralContactId} onCreated={async (entity) => { if(entity?.id){ setContacts(prev=>[...prev, entity]); setAllContacts(prev=>[...prev, entity]); setField('referralContactId', entity.id);} }}>
                                <DealFormSelect id="referralContactId" value={form.referralContactId} placeholder="Select contact" onValueChange={(v) => setField('referralContactId', v)} options={(allContacts.length?allContacts:contacts).map(c=>({ value: c.id, label: `${c.firstName||c.first_name||''} ${c.lastName||c.last_name||''}`.trim() + (c.email?` — ${c.email}`:'') || c.id }))} />
                            </DealQuickCreateField>
                            {errors.referralContactId && <span className="field-error">{errors.referralContactId}</span>}
                        </div>
                    )}
                    {form.dealStage === 'closedLost' && (
                    <>
                    <div className="form-group full-width" id="dealLostReasonGroup" data-field="dealLostReason">
                        <label htmlFor="dealLostReason">Lost Reason <span className="required-asterisk">*</span> <FieldHelpIcon fieldId="dealLostReason" /></label>
                        <Input unstyled type="text" id="dealLostReason" className="form-input" placeholder="Enter lost reason" value={form.dealLostReason} onChange={(e) => setField('dealLostReason', e.target.value)} autoFocus />
                        {errors.dealLostReason && <span className="field-error">{errors.dealLostReason}</span>}
                    </div>
                    <div className="form-group" id="dealLostReasonCategoryGroup" data-field="dealLostReasonCategory">
                        <label htmlFor="dealLostReasonCategory">Lost Category <span className="required-asterisk">*</span></label>
                        <DealFormSelect id="dealLostReasonCategory" value={form.dealLostReasonCategory} placeholder="Select category" onValueChange={(v) => setField('dealLostReasonCategory', v)} options={[{value:'price',label:'Price'},{value:'feature',label:'Feature'},{value:'timing',label:'Timing'},{value:'competition',label:'Competition'},{value:'other',label:'Other'}]} />
                        {errors.dealLostReasonCategory && <span className="field-error">{errors.dealLostReasonCategory}</span>}
                    </div>
                    </>
                    )}
                </div>
            </div>
        </div>
    );
}
